/* ==========================================================================
   TEAM BRUNE – Product page components
   ========================================================================== */
(() => {
  const theme = window.theme || {};
  const desktop = window.matchMedia('(min-width: 990px)');

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const priceMarkup = (variant) => {
    const money = theme.formatMoney;
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const classes = ['price', 'price--lg'];
    if (onSale) classes.push('price--sale');
    if (!variant.available) classes.push('price--sold-out');
    let html = `<div class="${classes.join(' ')}" data-price>`;
    if (onSale) {
      html += `<span class="price__current">${money(variant.price)}</span>`;
      html += `<s class="price__compare">${money(variant.compare_at_price)}</s>`;
    } else {
      html += `<span class="price__current">${money(variant.price)}</span>`;
    }
    return `${html}</div>`;
  };

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.groups = [...this.querySelectorAll('fieldset')];
      this.addEventListener('change', (event) => this.onChange(event));
      this.updateAvailability();
    }

    selectedOptions() {
      return this.groups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    onChange(event) {
      const input = event.target;
      const label = this.querySelector(`[data-selected-value="${input.dataset.optionIndex}"]`);
      if (label) label.textContent = input.value;

      const variant = this.findVariant(this.selectedOptions());
      this.updateAvailability();

      const form = document.getElementById(this.dataset.form);
      if (form && variant) {
        const idInput = form.querySelector('[data-variant-input]');
        if (idInput) idInput.value = variant.id;
      }
      if (variant) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      const variantLabel = variant
        ? this.groups
          .map((group, i) => (group.classList.contains('variants__group--single') ? null : variant.options[i]))
          .filter(Boolean)
          .join(' / ')
        : '–';
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { sectionId: this.dataset.section, variant: variant || null, label: variantLabel },
      }));
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      this.groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const available = this.variants.some((variant) =>
            variant.available && variant.options.every((value, i) => value === candidate[i]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }
  }
  define('variant-picker', VariantPicker);

  document.addEventListener('variant:change', ({ detail }) => {
    const root = document.querySelector(`[data-product-section="${detail.sectionId}"]`);
    if (!root) return;
    const { variant } = detail;
    const strings = theme.strings || {};

    const price = root.querySelector('[data-product-price]');
    if (price && variant) price.innerHTML = priceMarkup(variant);

    let label = strings.unavailable;
    if (variant) label = variant.available ? strings.addToCart : strings.soldOut;
    const disabled = !variant || !variant.available;
    root.querySelectorAll('[data-atc], [data-sticky-submit]').forEach((button) => {
      button.disabled = disabled;
      const text = button.querySelector('[data-atc-label]');
      if (text) text.textContent = label;
    });

    const stickyVariant = root.querySelector('[data-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = detail.label || (variant ? variant.title : '–');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    if (stickyPrice && variant) stickyPrice.textContent = theme.formatMoney(variant.price);
  });

  /* ---------- Add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-form-error]');
      if (!this.form || theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      const submit = this.form.querySelector('[data-atc]');
      if (!submit || submit.disabled || submit.classList.contains('is-loading')) return;
      const buttons = [submit, ...document.querySelectorAll(`sticky-atc[data-form="${this.form.id}"] [data-sticky-submit]`)];
      buttons.forEach((button) => {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      });
      if (this.error) this.error.hidden = true;
      try {
        await theme.cart.add(new FormData(this.form));
        theme.cart.open(submit);
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
      } finally {
        buttons.forEach((button) => {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        });
      }
    }
  }
  define('product-form', ProductForm);

  /* ---------- Gallery: swipe on mobile, zoom everywhere ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.items = [...this.track.children];
      this.counter = this.querySelector('[data-gallery-current]');
      this.dots = [...this.querySelectorAll('[data-gallery-goto]')];

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.setActive(Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1)));
          ticking = false;
        });
      }, { passive: true });

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.galleryGoto, 10))));

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom-index]');
        if (zoom) this.openZoom(parseInt(zoom.dataset.zoomIndex, 10), zoom);
      });

      document.addEventListener('variant:change', ({ detail }) => {
        if (detail.sectionId !== this.dataset.section || !detail.variant || !detail.variant.featured_media) return;
        const index = this.items.findIndex((item) => item.dataset.mediaId === String(detail.variant.featured_media.id));
        if (index >= 0) this.goTo(index);
      });
    }

    setActive(index) {
      if (index === this.active) return;
      this.active = index;
      if (this.counter) this.counter.textContent = index + 1;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    }

    goTo(index) {
      if (desktop.matches) {
        this.setActive(index);
        return;
      }
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
    }

    openZoom(index, trigger) {
      const modal = document.getElementById(`Zoom-${this.dataset.section}`);
      if (!modal || typeof modal.open !== 'function') return;
      modal.open(trigger);
      const target = modal.querySelector(`[data-zoom-item="${index}"]`);
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  }
  define('media-gallery', MediaGallery);

  document.addEventListener('click', (event) => {
    const img = event.target.closest('.zoom-modal__img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
    img.classList.toggle('is-zoomed');
  });

  document.addEventListener('pointermove', (event) => {
    const img = event.target.closest('.zoom-modal__img.is-zoomed');
    if (!img || event.pointerType !== 'mouse') return;
    const rect = img.getBoundingClientRect();
    img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
  });

  document.addEventListener('dialog:close', (event) => {
    event.target.querySelectorAll('.zoom-modal__img.is-zoomed').forEach((img) => img.classList.remove('is-zoomed'));
  });

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      const mainButton = this.form ? this.form.querySelector('[data-atc]') : null;
      if (!mainButton) return;
      // Position check instead of IntersectionObserver: a fast fling can jump the button
      // from below to above the viewport without ever intersecting.
      let ticking = false;
      const update = () => {
        const visible = mainButton.getBoundingClientRect().bottom < 0;
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
        ticking = false;
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      update();
      const submit = this.querySelector('[data-sticky-submit]');
      if (submit) {
        submit.addEventListener('click', () => {
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
          else this.form.submit();
        });
      }
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) {
          this.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
          if (typeof theme.observeReveal === 'function') theme.observeReveal(this);
        } else {
          this.closest('.shopify-section')?.remove();
        }
      } catch (error) {
        this.closest('.shopify-section')?.remove();
      }
    }
  }
  define('product-recommendations', ProductRecommendations);
})();
